import { IPatient } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { ImageManipulator } from '@capacitor-community/image-manipulator';
import { Camera } from '@capacitor/camera';
import { CapacitorPluginMlKitTextRecognition } from '@pantrist/capacitor-plugin-ml-kit-text-recognition';
import { DocumentScanner, ResponseType } from 'capacitor-document-scanner';
import { Logger } from 'src/app/common/logging/logger';
import { InsuranceNumberService } from './insurance-number.service';
import { TenantConfigService } from './tenant-config/tenant-config.service';
@Injectable({ providedIn: 'root' })
export class DocumentScannerService {
  static TAG = 'DocumentScannerService';
  constructor(
    private _tenantConfigService: TenantConfigService,
    private _logger: Logger
  ) {}

  /**
   * A function that is used to scan documents and read insurance numbers from eGK via OCR.
   *
   * Platform-Quirks / Problems:
   *
   * ## iOS
   * - cordova-plugin-document-scanner freezes when used in Stammdaten (eGK / ZuZaBe)
   * - VisionKit consumes a lot of memory which leads to
   *   WebViewProcess being killed / Alberta being restarted while in background
   *
   * ## Android
   * - cordova-plugin-document-scanner shows low memory warning dialog
   * */

  public async scanOneImageFromCamera(patient?: IPatient, attachmentTypeKey?: number): Promise<string> {
    const sourceType: DocumentScannerSourceType = 'Camera';
    this._logger.addBreadcrumb({
      message: `${DocumentScannerService.TAG} scan`,
      type: 'debug',
      data: {
        patientId: patient?._id,
        attachmentType: attachmentTypeKey,
      },
    });
    if (sourceType === 'Camera') {
      try {
        await this.checkCameraPermissions();
        // you can only set maxNumDocuments to 1 on Android, IOS always allows multiple
        const scannedDocuments = await DocumentScanner.scanDocument({
          maxNumDocuments: 1,
          responseType: ResponseType.Base64,
        });
        // We need only 1 image, so return the first one
        if (scannedDocuments?.scannedImages?.length) {
          const image = scannedDocuments.scannedImages[0];
          await this._extractOCRInformation(image, patient, attachmentTypeKey);
          return this._appendDataUrl(image);
        }
      } catch (error) {
        return null;
      }
    }
  }

  /**
   *
   * @returns The resized images native path. No base64!
   */
  public async scanOneImageFromCameraAndResize(): Promise<string> {
    const sourceType: DocumentScannerSourceType = 'Camera';
    if (sourceType === 'Camera') {
      try {
        await this.checkCameraPermissions();
        // you can only set maxNumDocuments to 1 on Android, IOS always allows multiple
        const scanResult = await DocumentScanner.scanDocument({
          maxNumDocuments: 1,
          responseType: ResponseType.ImageFilePath,
        });
        // We need only 1 image, so return the first one
        if (scanResult?.scannedImages?.length) {
          return await this._resizeImage(scanResult.scannedImages[0]);
        }
      } catch (error) {
        return null;
      }
    }
  }
  public async scanMultipleImagesFromCamera(
    patient?: IPatient,
    attachmentTypeKey?: number,
    amountOfPicturesAllowed: number = 1
  ): Promise<string[]> {
    const sourceType: DocumentScannerSourceType = 'Camera';
    this._logger.addBreadcrumb({
      message: `${DocumentScannerService.TAG} scan`,
      type: 'debug',
      data: {
        patientId: patient?._id,
        attachmentType: attachmentTypeKey,
      },
    });
    if (sourceType === 'Camera') {
      try {
        await this.checkCameraPermissions();
        // you can only set maxNumDocuments to 1 on Android, IOS always allows multiple
        const scannedDocuments = await DocumentScanner.scanDocument({
          maxNumDocuments: amountOfPicturesAllowed,
          responseType: ResponseType.Base64,
        });

        if (scannedDocuments?.scannedImages?.length > 0) {
          return scannedDocuments.scannedImages;
        }
      } catch (error) {
        return null;
      }
    }
  }

  private async checkCameraPermissions(): Promise<void> | never {
    let permissions = await Camera.checkPermissions();

    if (permissions.camera !== 'granted') {
      permissions = await Camera.requestPermissions({ permissions: ['camera'] });
    }

    if (permissions.camera === 'denied') {
      throw new Error('Camera Permission missing!');
    }
  }

  /**
   * Resizes an image according to tenant configuration setting and returns a temp image path
   * using ImageManipulator plugin https://github.com/capacitor-community/image-manipulator
   * @param imagePath - Native file path to the target image
   * @returns The resized image's native file path, or null if resizing fails
   */
  private async _resizeImage(imagePath: string): Promise<string | null> {
    const { quality, maxHeight, maxWidth } = await this._tenantConfigService.getImageManipulatorConfig();
    const resizeResult = await ImageManipulator.resize({
      imagePath: imagePath,
      quality: quality,
      maxHeight: maxHeight,
      maxWidth: maxWidth,
    });
    if (resizeResult.resized) {
      return resizeResult.imagePath;
    } else {
      return null;
    }
  }

  /**
   * Extracts insurance information from a health insurance card image using OCR.
   * Only runs OCR when `attachmentType` is 0 (HealthInsuranceCard).
   * Extracts and sets `numberOfInsured` and `payerInstitutionCode` on the patient object.
   *
   * @param image - Base64 encoded image string
   * @param patient - Patient object to update with extracted information
   * @param attachmentType - Attachment type key (0 = HealthInsuranceCard)
   */
  private async _extractOCRInformation(image: string, patient: IPatient, attachmentType: number = null): Promise<void> {
    this._logger.addBreadcrumb({
      message: `${DocumentScannerService.TAG} received image from plugin`,
      type: 'debug',
      data: {
        size: image?.length,
        triggersOCR: attachmentType === 0, // HealthInsuranceCard
      },
    });
    if (attachmentType === 0) {
      // HealthInsuranceCard
      try {
        const ocrResult = await CapacitorPluginMlKitTextRecognition.detectText({ base64Image: image });
        this._logger.addBreadcrumb({
          message: `${DocumentScannerService.TAG} received OCR result`,
          type: 'debug',
          data: {
            sourceImageSize: image?.length,
            ocrResult: ocrResult.text,
          },
        });
        if (ocrResult.text) {
          const insuranceNumbers = ocrResult.blocks
            .filter(block => InsuranceNumberService.isNumberOfInsured(block.text))
            .map(block => block.text);
          if (insuranceNumbers.length && insuranceNumbers[0] && insuranceNumbers[0].toString() !== '0') {
            patient.payer.numberOfInsured = insuranceNumbers[0];
          }
          const institutionIds = ocrResult.blocks
            .map(block => InsuranceNumberService.tryExtractingInstitutionCode(block.text))
            .filter(id => id !== null);
          if (institutionIds.length > 0) {
            if (institutionIds.length === 1) {
              patient.payerInstitutionCode = institutionIds[0];
            } else {
              const firstId = institutionIds[0];
              const allDetectedIdsAreEqual = institutionIds.every(id => id === firstId);
              if (allDetectedIdsAreEqual) {
                patient.payerInstitutionCode = firstId;
              }
            }
          }
        }
      } catch (e) {
        this._logger.error('document-scanner: OCR failed', e);
      }
    }
  }
  /**
   * Ensures the image string has a proper data URL prefix.
   * @param image - Base64 image string, with or without data URL prefix
   * @returns Image string with `data:image/jpeg;base64,` prefix
   */
  private _appendDataUrl(image: string) {
    return image.startsWith('data:') ? image : `data:image/jpeg;base64,${image}`;
  }
}

export type DocumentScannerSourceType = 'Camera' | 'Gallery';
